import type { RobotState } from '@fabricos/protocol-ts';
import type { TrailPoint } from '@/store/fleet';

/**
 * Persistent trail history — IndexedDB, because this grows far beyond
 * localStorage territory. Points buffer in memory and commit every few
 * seconds; a byte budget (Settings → Storage) prunes oldest-first after
 * each commit. Approximate accounting: records are fixed-shape, so
 * count × BYTES_PER_RECORD is honest enough for a budget gauge.
 */
const DB_NAME = 'fabricos-telemetry';
const DB_VERSION = 1;
const STORE = 'trail_points';
const COMMIT_MS = 5_000;
/** Rough IndexedDB footprint per record (fields + keys + index overhead). */
export const BYTES_PER_RECORD = 120;

interface TrailRecord {
  robotId: string;
  ts: number;
  lat: number;
  lon: number;
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const store = req.result.createObjectStore(STORE, { autoIncrement: true });
        store.createIndex('ts', 'ts');
        store.createIndex('robotId', 'robotId');
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }
  return dbPromise;
}

/* ------------------------- write path ------------------------- */

const pending: TrailRecord[] = [];
const lastWritten = new Map<string, { lat: number; lon: number }>();
let commitTimer: number | null = null;
/** MB budget provider — set once by the settings store to avoid a cycle. */
let budgetMbProvider: () => number = () => 1024;

export function setTrailBudgetProvider(fn: () => number): void {
  budgetMbProvider = fn;
}

/** Queue movement points from a state batch. Cheap; commits on a timer. */
export function queueTrailPoints(batch: RobotState[]): void {
  for (const s of batch) {
    if (!s.position) continue;
    const last = lastWritten.get(s.robot_id);
    if (
      last &&
      Math.abs(last.lat - s.position.lat) <= 1e-6 &&
      Math.abs(last.lon - s.position.lon) <= 1e-6
    ) {
      continue;
    }
    lastWritten.set(s.robot_id, { lat: s.position.lat, lon: s.position.lon });
    pending.push({
      robotId: s.robot_id,
      ts: Number(s.ts_ms) || Date.now(),
      lat: s.position.lat,
      lon: s.position.lon,
    });
  }
  if (pending.length > 0 && commitTimer === null) {
    commitTimer = window.setTimeout(() => {
      commitTimer = null;
      void commit();
    }, COMMIT_MS);
  }
}

async function commit(): Promise<void> {
  if (pending.length === 0) return;
  const batch = pending.splice(0, pending.length);
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      const store = tx.objectStore(STORE);
      for (const rec of batch) store.add(rec);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    await enforceBudget(budgetMbProvider());
  } catch (err) {
    console.error('[trailDb] commit failed', err);
  }
}

/** Prune oldest records until under 90% of the MB budget. */
export async function enforceBudget(budgetMb: number): Promise<number> {
  const db = await openDb();
  const count = await new Promise<number>((resolve, reject) => {
    const req = db.transaction(STORE).objectStore(STORE).count();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  const budgetBytes = budgetMb * 1024 * 1024;
  const bytes = count * BYTES_PER_RECORD;
  if (bytes <= budgetBytes) return 0;
  const excess = Math.ceil((bytes - budgetBytes * 0.9) / BYTES_PER_RECORD);
  return new Promise<number>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const idx = tx.objectStore(STORE).index('ts');
    let deleted = 0;
    const cursorReq = idx.openCursor();
    cursorReq.onsuccess = () => {
      const cursor = cursorReq.result;
      if (cursor && deleted < excess) {
        cursor.delete();
        deleted++;
        cursor.continue();
      }
    };
    tx.oncomplete = () => resolve(deleted);
    tx.onerror = () => reject(tx.error);
  });
}

/* ------------------------- read path ------------------------- */

/** Load recent points grouped by robot (ascending ts), capped per robot. */
export async function loadRecentTrails(
  sinceMs: number,
  maxPerRobot = 600,
): Promise<Record<string, TrailPoint[]>> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const out: Record<string, TrailPoint[]> = {};
    const idx = db.transaction(STORE).objectStore(STORE).index('ts');
    const req = idx.openCursor(IDBKeyRange.lowerBound(sinceMs));
    req.onsuccess = () => {
      const cursor = req.result;
      if (!cursor) {
        for (const id of Object.keys(out)) {
          out[id] = out[id]!.slice(-maxPerRobot);
        }
        resolve(out);
        return;
      }
      const rec = cursor.value as TrailRecord;
      (out[rec.robotId] ??= []).push({
        lat: rec.lat,
        lon: rec.lon,
        ts_ms: BigInt(Math.round(rec.ts)),
      });
      cursor.continue();
    };
    req.onerror = () => reject(req.error);
  });
}

/* ------------------------- maintenance ------------------------- */

export interface TrailStats {
  records: number;
  approxBytes: number;
  originUsageBytes: number | null;
  originQuotaBytes: number | null;
}

export async function trailStats(): Promise<TrailStats> {
  const db = await openDb();
  const records = await new Promise<number>((resolve, reject) => {
    const req = db.transaction(STORE).objectStore(STORE).count();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  let originUsageBytes: number | null = null;
  let originQuotaBytes: number | null = null;
  try {
    const est = await navigator.storage?.estimate?.();
    originUsageBytes = est?.usage ?? null;
    originQuotaBytes = est?.quota ?? null;
  } catch {
    /* estimate unsupported */
  }
  return { records, approxBytes: records * BYTES_PER_RECORD, originUsageBytes, originQuotaBytes };
}

/** Delete records older than `days` days. Returns deleted count. */
export async function deleteOlderThan(days: number): Promise<number> {
  const cutoff = Date.now() - days * 86_400_000;
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const idx = tx.objectStore(STORE).index('ts');
    let deleted = 0;
    const req = idx.openCursor(IDBKeyRange.upperBound(cutoff));
    req.onsuccess = () => {
      const cursor = req.result;
      if (cursor) {
        cursor.delete();
        deleted++;
        cursor.continue();
      }
    };
    tx.oncomplete = () => resolve(deleted);
    tx.onerror = () => reject(tx.error);
  });
}

export async function deleteAllTrails(): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).clear();
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  lastWritten.clear();
}

export function fmtBytes(n: number): string {
  if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(2)} GB`;
  if (n >= 1024 ** 2) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  if (n >= 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${n} B`;
}
